import { Modal } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setConfigurableFolder, setIsConfigureFolderOpen } from 'store/slices/dataPermissionsSlice';
import classes from './ConfigureFolder.module.scss';
import api from 'api';
import { IGroup } from 'interfaces';
import PermissionsByFolder from 'components/PermissionsByFolder/PermissionsByFolder';
import { defaultGroups } from 'store/reducers/usersGroupCreator';
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import cn from 'classnames';
import { getTheme } from 'helpers/getTheme';

type ConfigureFolderType = {
  isOpen: boolean;
};

const ConfigureFolder = ({isOpen}: ConfigureFolderType) => {
  const [defaultPermissions, setDefaultPermissions] = React.useState<IGroup[]>([]);
  const [rolesPermissionsOfFolder, setRolesPermissionsOfFolder] = React.useState<IGroup[]>([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = React.useState<boolean>(false);
  const [isTheSamePermissions, setIsTheIsTheSamePermissions] = React.useState(true);  
  const [isUpdatingPermissions, setIsUpdatingPermissions] = React.useState(false);  

  const { configurableFolder } = useAppSelector((store) => store.permissions);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { groups } = useAppSelector((state) => state.dataUsers);
  const { messageApi } = useAppSelector((store) => store.windowState);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();  

  const getPermissionsByFolder = async () => {
    setIsLoadingPermissions(true); 
    try {
      const permissions = await api.getRolesPermissions({room_id: dataRoom!.id, folder_id: configurableFolder?.id ? configurableFolder?.id : null });
      const rightGroupsWithPermissions = groups.map(group => {
        const correctPermissions = permissions.data.find(el => el.group_id === group.group_id)?.permissions;
        return { ...group, permissions: correctPermissions || [] };
      })
      .sort((a, b) => {
        const includeAName = defaultGroups.includes(String(a.first_name));
        const includeBName = defaultGroups.includes(String(b.first_name))
        if (includeBName && includeAName){
          return defaultGroups.indexOf(String(a.first_name)) - defaultGroups.indexOf(String(b.first_name));
        }
        if(includeAName) return -1;
        if(includeBName) return 1;
        return String(a.first_name).localeCompare(String(b.first_name))
      });
      setDefaultPermissions(rightGroupsWithPermissions as IGroup[]);
      setRolesPermissionsOfFolder(rightGroupsWithPermissions as IGroup[]);
    } catch {} finally {
      setIsLoadingPermissions(false);
    }
  };

  React.useEffect(() => {
    if (groups.length > 0) {
      getPermissionsByFolder();
    }
  }, [groups]);

  React.useEffect(() => {
    return () => {
      dispatch(setConfigurableFolder(null));
    }
  }, []);
  
  React.useEffect(() => {
    !isLoadingPermissions && checkPermissions();
  },[rolesPermissionsOfFolder]);

  const checkPermissions = () => {
    const comparePremissions = rolesPermissionsOfFolder.map(role => {      
      const currentPermissions = role.permissions || [];    
      const sortedCurrentPermissions = [...currentPermissions]?.sort().join(',');
      const defaultArr = defaultPermissions.find(def_role => def_role.group_id === role.group_id)?.permissions || [];
      const sortedDefaultPermissions = [...defaultArr].sort().join(',');
      return sortedDefaultPermissions === sortedCurrentPermissions;
    });    

    if (comparePremissions.includes(false)) setIsTheIsTheSamePermissions && setIsTheIsTheSamePermissions(false)
    else setIsTheIsTheSamePermissions && setIsTheIsTheSamePermissions(true);
  };
  
  
  const sendRolesPermissions = async () => {
    let arrGroupNames: any[] = [];    
    const currentFolderId = configurableFolder?.id ? configurableFolder.id : null;

    await Promise.all(rolesPermissionsOfFolder.map((group) => {
      const sortedCurrent = group.permissions || [];
      const sortedCurrentPermissions = [...sortedCurrent].sort().join(',');
      const defPermissions = defaultPermissions.find(def_role => def_role.group_id === group.group_id)?.permissions || [];      
      const sortedDefPermissions = [...defPermissions].sort().join(',');      
      
      return sortedDefPermissions !== sortedCurrentPermissions && api.setFolderPermission({
        permission_types: sortedCurrent as string[],
        folder_id: currentFolderId!,
        room_id: dataRoom?.id!,
        group_id: group?.group_id!,
        details: {
          before: defPermissions,
          after: sortedCurrent,
          folder_id: currentFolderId!,
          folder_name: configurableFolder?.name!,
          group_name: group?.group_name,
          group_role: group?.role?.name || '-',
          group_id: group?.group_id,
        }
      })
      .then(value => {
        arrGroupNames.push(group.group_name);
      })
      .catch(e => {
        messageApi?.warning(`${t('Documents.error.permissionsFailedRole')}' ${defaultGroups.includes(group.group_name!) ? t(`Users.table.${group.group_name}`) : group.group_name}'`);
      })
    }));
    messageApi?.success(`
      ${t('Documents.success.permissionsRolesSuccess')}
      ${t(`Documents.success.${arrGroupNames.length > 1 ? 'roles' : 'role'}`)}
      ${arrGroupNames.map(group => defaultGroups.includes(group) ? t(`Users.table.${group}`) : group).join(', ')}
    `, 10);
  };

  const onSendPermissions = async () => {    
    setIsUpdatingPermissions(true);
    try {
      await sendRolesPermissions();
      setIsTheIsTheSamePermissions(true);
      setDefaultPermissions(rolesPermissionsOfFolder);
    } catch (e) {
    } finally {
      setIsUpdatingPermissions(false);
    }
  };  

  return (
    <Modal
      open={isOpen}
      onCancel={() => dispatch(setIsConfigureFolderOpen(false))}
      title={`${t('rightSideBar.permissionsSection.permissionsOfFolder')} ${configurableFolder?.name}`}
      className={cn(classes.configureModal, getTheme())}
      centered
      confirmLoading={isUpdatingPermissions}
      cancelText={t('Users.modals.configureRoles.close')}
      okText={t('Users.modals.configureRoles.saveChanges')}
      onOk={onSendPermissions}
      okButtonProps={{disabled: isTheSamePermissions}}
    >
      <div className={classes.configureTitle}>{t('rightSideBar.permissionsSection.permissionsByRoles')}</div>
      
      <PermissionsByFolder
        isLoading={isLoadingPermissions}
        permissionsOfFolder={rolesPermissionsOfFolder}
        setPermissionsOfFolder={setRolesPermissionsOfFolder}
        parent={'configureFolder'}
      />

      {isUpdatingPermissions && <UploadingComponent />}
    </Modal>
  );
}

export default ConfigureFolder;
